/**
 * An <img> for a protected image: fetched with the session token through
 * the API (a plain `src` can't carry it), shown from a blob URL that is freed
 * on unmount. Shows a soft skeleton while loading and nothing on failure.
 *
 * `useAuthImageUrl` is the same fetch as a hook, for components that take a
 * `src` (Avatar). Children's photos are only served this way.
 */

import { useEffect, useState } from "react";

import { api } from "@/lib/api/client";

type AuthImageState = { src: string | null; failed: boolean };

export function useAuthImageUrl(path: string | null | undefined): AuthImageState {
  const [state, setState] = useState<AuthImageState>({ src: null, failed: false });

  useEffect(() => {
    setState({ src: null, failed: false });
    if (!path) return;
    const controller = new AbortController();
    let url: string | null = null;
    api
      .blob(path, { signal: controller.signal })
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setState({ src: url, failed: false });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ src: null, failed: true });
      });
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);

  return state;
}

export function AuthImage({ path, alt, className }: { path: string; alt: string; className?: string }) {
  const { src, failed } = useAuthImageUrl(path);
  if (failed) return null;
  if (!src) return <span className={`skeleton ${className ?? ""}`} style={{ display: "block", width: 160, height: 120 }} aria-hidden />;
  return <img className={className} src={src} alt={alt} loading="lazy" />;
}
